// Step 2: search for integrations, add them to the selection, and show what is
// currently linked along with each one's sync scope.

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Flex, SearchInput } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import { t, Trans } from '@lingui/macro';

import {
  AddResultButton,
  ChipShelf,
  ChipXBtn,
  DesignSection,
  DesignSectionHead,
  FieldError,
  IntegrationChip,
  ResultSub,
  SearchResultInfo,
  SearchResultMeta,
  SearchResultName,
  SearchResultRow,
  SearchResultsContainer,
  SelectedLabel,
  SelectionNudge,
  StepBadge,
  StepHeading,
  StepIntro,
  SyncBadge,
} from '../style';
import { integrationShape } from './formPropTypes';
import {
  hasLocationAttributes,
  locationAttributesOf,
} from './sharedSettingModel';

const syncLabelFor = (integration) =>
  hasLocationAttributes(integration) ? t`Attributes/Lifecycle` : t`Lifecycle`;

const IntegrationsPicker = ({
  search,
  selectedIntegrations,
  integError = '',
  loading = false,
  onAdd,
  onRemove,
}) => {
  const selectedIds = new Set(selectedIntegrations.map((i) => i.id));
  // Every integration is eligible — those without location-level attributes join
  // for lifecycle sync only. The only exclusion is what is already selected.
  const filteredResults = search.results.filter((r) => !selectedIds.has(r.id));
  const emptyResultsMessage =
    search.results.length === 0
      ? t`No integrations match.`
      : t`All results already selected.`;

  return (
    <DesignSection>
      <DesignSectionHead>
        <StepHeading>
          <StepBadge>2</StepBadge>
          <Trans>Integrations</Trans>
        </StepHeading>
        <StepIntro>
          <Trans>
            Choose two or more integrations to share this configuration. The
            first one selected becomes the <strong>source of truth</strong>.
            Integrations with no location-level attributes can be linked for{' '}
            <strong>Lifecycle</strong> sync only.
          </Trans>
        </StepIntro>
      </DesignSectionHead>
      <div>
        <Flex flexDirection="column" marginBottom={1}>
          <SearchInput
            value={search.term}
            width={32}
            label={t`Search integrations`}
            name="integration-search"
            onChange={(e) => search.changeTerm(e.target.value)}
            onSearch={search.search}
            size="small"
            searchDisabled={search.isSearching}
          />
        </Flex>

        {search.isSearching && (
          <Flex marginTop={0.5}>
            <Loading size="small" title={t`Searching...`} />
          </Flex>
        )}

        {!search.isSearching && search.hasSearched && (
          <SearchResultsContainer>
            {filteredResults.length === 0 ? (
              <SearchResultRow>
                <P size="small" shade={1} marginBottom={0}>
                  {emptyResultsMessage}
                </P>
              </SearchResultRow>
            ) : (
              filteredResults.map((result) => {
                const attrCount = locationAttributesOf(result).length;
                return (
                  <SearchResultRow key={result.id}>
                    <SearchResultInfo>
                      <SearchResultName>
                        {result.attributes.name}
                      </SearchResultName>
                      <SearchResultMeta>
                        <SyncBadge
                          lifecycleOnly={attrCount === 0}
                          data-testid={`result-sync-label-${result.id}`}
                        >
                          {syncLabelFor(result)}
                        </SyncBadge>
                        {attrCount > 0 && (
                          <ResultSub>
                            {attrCount} attribute
                            {attrCount !== 1 ? 's' : ''}
                          </ResultSub>
                        )}
                      </SearchResultMeta>
                    </SearchResultInfo>
                    <AddResultButton
                      text={t`Add`}
                      kind="secondary"
                      size="small"
                      onClick={() => onAdd(result)}
                    />
                  </SearchResultRow>
                );
              })
            )}
          </SearchResultsContainer>
        )}

        <div>
          <SelectedLabel spaced={selectedIntegrations.length > 0}>
            <Trans>Selected integrations</Trans> ({selectedIntegrations.length})
            {selectedIntegrations.length === 1 && (
              <SelectionNudge>
                &middot; <Trans>Select one more to continue</Trans>
              </SelectionNudge>
            )}
          </SelectedLabel>
          <ChipShelf>
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading ? (
              <Loading size="small" title={t`Loading integrations...`} />
            ) : selectedIntegrations.length === 0 ? (
              <P size="small" shade={1} marginBottom={0}>
                <Trans>
                  No integrations selected yet. Pick at least two from the
                  search above.
                </Trans>
              </P>
            ) : (
              selectedIntegrations.map((integ) => (
                <IntegrationChip key={integ.id}>
                  {integ.attributes.name}
                  <SyncBadge
                    lifecycleOnly={!hasLocationAttributes(integ)}
                    data-testid={`chip-sync-label-${integ.id}`}
                  >
                    {syncLabelFor(integ)}
                  </SyncBadge>
                  <ChipXBtn
                    aria-label={`Remove ${integ.attributes.name}`}
                    onClick={() => onRemove(integ.id)}
                  >
                    &times;
                  </ChipXBtn>
                </IntegrationChip>
              ))
            )}
          </ChipShelf>
        </div>

        {integError && <FieldError>{integError}</FieldError>}
      </div>
    </DesignSection>
  );
};

IntegrationsPicker.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  search: PropTypes.object.isRequired,
  selectedIntegrations: PropTypes.arrayOf(integrationShape).isRequired,
  integError: PropTypes.string,
  loading: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
export default memo(IntegrationsPicker);
